/// <reference types="node" />
/** @jsx jsx */
import React, { memo, type CSSProperties } from 'react';
import { styled, css, jsx } from '@compiled/react';

// eslint-disable-next-line jira/import-whitelist
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

// eslint-disable-next-line jira/import-whitelist
import { isChrome } from '@jira/platform__browser/src/index.tsx';
import { defineMessage, useIntl } from '@atlassian/jira-intl';

// eslint-disable-next-line jira/import-whitelist
import { FallbackIndicator } from './FallbackIndicator.tsx';

const loadingMessage = defineMessage({
	id: 'skeletons.common.loading',
	defaultMessage: 'Loading',
	description: 'Alt text of skeleton image',
});

const skeletonImageStyles = css({
	display: 'block',
	boxSizing: 'border-box',
	width: '100%',
	borderStyle: 'solid',
	borderColor: 'transparent',
	borderLeftWidth: '40px',
	borderRightWidth: '40px',

	/* Fix FF 2x scale bug on high res devices https://bugzilla.mozilla.org/show_bug.cgi?id=986403 */
	'@media (min--moz-device-pixel-ratio: 2)': {
		borderLeftWidth: '20px',
		borderRightWidth: '20px',
	},
});

const skeletonImageBorderlessStyles = css({
	borderTopWidth: 0,
	borderRightWidth: 0,
	borderBottomWidth: 0,
	borderLeftWidth: 0,

	/* Keep the borderless variant borderless when the Firefox high-res scaling fix applies. */
	'@media (min--moz-device-pixel-ratio: 2)': {
		borderLeftWidth: 0,
		borderRightWidth: 0,
	},
});

type Props = {
	src: string;
	// css?: string | string[];
	className?: string;
	/**
	 * Removes the default transparent side border for skeletons whose SVG already includes page
	 * spacing.
	 */
	hideBorder?: boolean;
	/**
	 * Inline styles to apply to the image container, for example, to set a fixed container height for component
	 * skeletons.
	 */
	containerStyles?: CSSProperties;
	testId?: string;
};

export const AppSkeletonImage: React.MemoExoticComponent<
	({ src, className, hideBorder, containerStyles, testId }: Props) => JSX.Element
> = memo(({ src, className, hideBorder = false, containerStyles, testId }: Props): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<UFOLoadHold name="app-skeleton">
			<SkeletonWrapper
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={containerStyles}
			>
				{process.env.NODE_ENV !== 'production' && isChrome() ? (
					/* during dev use <object> instead of <img> to easily debug */
					<object
						data={src}
						data-testid={testId}
						type="image/svg+xml"
						css={[skeletonImageStyles, hideBorder && skeletonImageBorderlessStyles]}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={className}
						// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
						aria-label={`${formatMessage(loadingMessage)}...`}
					>
						{}
						{formatMessage(loadingMessage)}...
					</object>
				) : (
					<img
						src={src}
						data-testid={testId}
						alt={`${formatMessage(loadingMessage)}...`}
						css={[skeletonImageStyles, hideBorder && skeletonImageBorderlessStyles]}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
						className={className}
					/>
				)}
			</SkeletonWrapper>
			<FallbackIndicator />
		</UFOLoadHold>
	);
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonWrapper = styled.div({
	overflow: 'hidden',
	width: '100%',
	height: '100%',

	/* FF bug with high res devices https://bugzilla.mozilla.org/show_bug.cgi?id=986403 */
	'@media (min--moz-device-pixel-ratio: 2)': {
		width: '50%',
		height: '50%',
		transform: 'scale(2)',
		transformOrigin: '0 0',
	},
});
